import { Injectable, inject } from '@angular/core';
import { httpOptions } from '../_helpers/utils';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { User, UserIndex } from '../_models/user';
import { PaginationData } from '../_models/pagination-data.model';
import { Role } from '../_models/role';
import { CreateLaboratory, Laboratory } from '../_models/laboratory';

export interface LabResponse {
  lab?: Laboratory;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LaboratoryService {
  private readonly http = inject(HttpClient);

  httpOptions = httpOptions;

  private readonly pageNumber$ = new BehaviorSubject<number>(null);
  private readonly pageSize$ = new BehaviorSubject<number>(null);
  private readonly createLabCache$ = new BehaviorSubject<CreateLaboratory>(
    null,
  );

  public get retrieveAndClearUnsavedLabCache(): CreateLaboratory {
    const cached = this.createLabCache$.value;
    this.clearUnsavedCache();
    return cached;
  }

  clearUnsavedCache(): void {
    this.createLabCache$.next(null);
  }

  onUpdateCreateLabCache = (lab: CreateLaboratory): void => {
    this.createLabCache$.next(lab);
  };

  onUpdatePageNumber = (pageNumber: number): void => {
    this.pageNumber$.next(pageNumber);
  };

  onUpdatePageSize = (pageSize: number): void => {
    this.pageSize$.next(pageSize);
  };

  public get paginationCache(): { pageNumber: number; pageSize: number } {
    if (this.pageNumber$.value !== null && this.pageSize$.value !== null) {
      return {
        pageNumber: this.pageNumber$.value,
        pageSize: this.pageSize$.value,
      };
    } else {
      return null;
    }
  }

  addLab(labName: string, orgId?: string): Observable<Laboratory> {
    return this.http
      .post<Laboratory>(
        `${environment.apiUrl}/laboratories`,
        {
          laboratoryName: labName,
          organisationId: orgId || null,
        },
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          if (response.body == null) {
            throw { status: response.status, message: 'Something went wrong', code: null };
          }
          return response.body;
        }),
      );
  }

  getLab(id: number): Observable<Laboratory> {
    return this.http
      .get<Laboratory>(
        `${environment.apiUrl}/laboratories/${id}`,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  updateLab(labId: number, labName: string): Observable<string> {
    return this.http
      .patch<string>(
        `${environment.apiUrl}/laboratories/${labId}`,
        {
          laboratoryName: labName,
        },
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          switch (response.status) {
            case 200:
              return 'The Laboratory has been updated.';
            case 201:
              return 'Laboratory update request is pending approval; the status can be checked on the approvals page.';
            default:
              throw { status: response.status, message: 'Something went wrong', code: null };
          }
        }),
      );
  }

  getLabMembers(
    labId: number,
    nonPaginated = false,
    sortBy = 'LastName',
    sortOrder = 'asc',
    pageNumber = 1,
    pageSize = 5,
    userRole: Role = null,
  ): Observable<UserIndex> {
    let url = `${environment.apiUrl}/users/${labId}/members?OrderBy=${sortBy}&OrderByDirection=${sortOrder}&PageNumber=${pageNumber}&PageSize=${pageSize}`;
    if (userRole) {
      url += `&Role=${userRole}`;
    }
    if (nonPaginated) {
      url += `&NonPaginated=true`;
    }
    return this.http.get<User[]>(url, this.httpOptions).pipe(
      map((response) => {
        // Set pagination data
        const paginationData = JSON.parse(
          response.headers.get('X-Pagination'),
        ) as PaginationData;
        const users = response.body;
        return { paginationData, users };
      }),
    );
  }

  // PATCH /laboratories/{userId}/{labId}
  removeUserFromLab(userId: number, labId: number): Observable<string> {
    return this.http
      .patch<string>(
        `${environment.apiUrl}/laboratories/${userId}/${labId}`,
        null,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          switch (response.status) {
            case 200:
              return 'User has been removed from the laboratory.';
            case 201:
              return 'Remove user from laboratory request is pending approval; the status can be checked on the approvals page.';
            default:
              throw { status: response.status, message: 'Something went wrong', code: null };
          }
        }),
      );
  }

  // PATCH /laboratories/add-user/{userId}/{labId}
  addUserToLab(userId: number, labId: number): Observable<string> {
    return this.http
      .patch<string>(
        `${environment.apiUrl}/laboratories/add-user/${userId}/${labId}`,
        null,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          switch (response.status) {
            case 200:
              return 'User has been added to the laboratory.';
            case 201:
              return 'Add user to laboratory request is pending approval; the status can be checked on the approvals page.';
            default:
              throw { status: response.status, message: 'Something went wrong', code: null };
          }
        }),
      );
  }

  // GET /users/{labId}/available-members
  getAvailableUsersForLab(labId: number, orgId?: number): Observable<User[]> {
    let url = `${environment.apiUrl}/users/${labId}/available-members`;
    if (orgId) {
      url = url + `?&organisationId=${orgId}`;
    }
    return this.http.get<User[]>(url, this.httpOptions).pipe(
      map((response) => {
        return response.body;
      }),
    );
  }

  deleteLab(labId: number): Observable<string> {
    return this.http
      .delete<string>(`${environment.apiUrl}/laboratories/${labId}`)
      .pipe(
        map(() => {
          return 'Delete laboratory request is pending approval; the status can be checked on the approvals page.';
        }),
      );
  }
}
